import {Grid, useReachGoalCallback} from '@logistics-frontend/blocks'
import {Map} from '@logistics-frontend/blocks/lpc/map'
import {Route, Router as MemoryRouter, useHistory, useParams} from '@logistics-frontend/blocks/lpc/react-router'
import {Tab, Tabs} from '@logistics-frontend/blocks/lpc/tabs'
import {useI18n} from '@logistics-frontend/core/i18n'
import {finalizeRumCounter} from '@logistics-frontend/modules/rum'
import {useZoneInfoQuery} from '@yandex-taxi/react-integration-api'
import {Collection} from '@yandex-taxi/react-ymaps'
import React, {useCallback, useRef} from 'react'

import {useConfig} from 'src/config'

import {DraftPoints, OrderPoints} from '../shared/points'
import {classes, tabsClasses} from './classes'
import {MAP_OPTIONS, MAP_STATE} from './consts'
import {Form} from './form'
import {Header} from './header'
import {useMapBounds} from './hooks'
import MapControls from './map-controls'
import MapEvents from './MapEvents'
import {Notifications} from './notifications'
import {History, Orders} from './orders'
import {Title} from './title'
import {TabName} from './types'

const metrikaTabMap: Record<TabName, string> = {
  [TabName.DRAFT]: 'Order-Button-ClickDraft',
  [TabName.ORDERS]: 'Order-Button-ClickMyOrders',
}

const App: React.FC = () => {
  const t = useI18n()
  const {replace} = useHistory()
  const {tab, id} = useParams()
  const ref = useRef(null)
  const reachGoal = useReachGoalCallback()
  const changeHandler = useCallback(
    (_: React.ChangeEvent<{}>, nextTab: TabName) => {
      replace(nextTab)
      reachGoal(metrikaTabMap[nextTab])
      finalizeRumCounter()
    },
    [reachGoal, replace],
  )
  const [bounds, setBounds] = useMapBounds(tab)
  const {status = 'error'} = useZoneInfoQuery()
  const isNotAvailableZone = status === 'error'
  // изменим центр по-умолначию, на тот что пришел из конфига или на Мск, если зона не обслуживается
  const defaultMapCenter = useConfig().initialProps.zonePoint
  MAP_STATE.center = isNotAvailableZone ? MAP_STATE.center : defaultMapCenter

  return (
    <>
      <Header />
      <Notifications />
      <Grid className={classes.container} item xs>
        <div className={classes.form}>
          <Title />
          <Tabs classes={tabsClasses} onChange={changeHandler} value={tab}>
            <Tab label={t('order.tabs.draft', 'Сделать заказ')} value={TabName.DRAFT} />
            <Tab label={t('order.tabs.orders', 'Мои заказы')} value={TabName.ORDERS} />
          </Tabs>
          <div className={classes.formWrap}>
            <Route path={TabName.DRAFT}>
              <Form />
            </Route>
            <Route path={TabName.ORDERS}>
              <Orders activeOrderID={id} />
              <History activeOrderID={id} />
            </Route>
          </div>
        </div>
        <div ref={ref} className={classes.map}>
          <Map options={MAP_OPTIONS} rootRef={ref} state={MAP_STATE}>
            <MapEvents bounds={bounds} tab={tab} />
            <MapControls id={id} tab={tab} />
            <Collection>
              <Route path={TabName.DRAFT}>
                <DraftPoints setBounds={setBounds} />
              </Route>
              <Route path={TabName.ORDERS}>
                <OrderPoints orderID={id} setBounds={setBounds} />
              </Route>
            </Collection>
          </Map>
        </div>
      </Grid>
    </>
  )
}

const RouterApp: React.FC = () => (
  <MemoryRouter defaultPath={TabName.DRAFT}>
    <App />
  </MemoryRouter>
)

export default RouterApp
